// Layer 3 seed (Cost & revenue, screenshot-grade — paul-tarp.A20 #422):
// the seven complete production days 1–7 Jun 2026 (D25), unit costs, the
// smelter economics used for revenue-by-concentrate, and the improvement
// register.
//
// [syn] screenshot-grade seed — every figure below is illustrative except
// the reagent dose rates, BM01's continuous power draw, and the Zn
// concentrate Pb-contamination spec, which are documented facts (v19 M12
// §4–5, M13 §4–6; cited in docs/logic/cost/overview.md, not on the glass).

import { JUNE_DAILY_PLAN_DST } from '@/versions/v1/shared/june-tonnage.derive';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';

export const COST_SEED_PROVENANCE = '[syn] screenshot-grade cost & revenue seed — paul-tarp.A20';

// ---------------------------------------------------------------------------
// Production days
// ---------------------------------------------------------------------------

export type DayRecoveries = { zn: number; pb: number; ag: number };
export type DayConcGrades = { zn: number; pb: number; ag: number };

export type CostDaySeed = {
  date: string;
  tonnesMilled: number;
  recoveries: DayRecoveries;
  concGrades: DayConcGrades;
  /** Assayed Pb content in the Zn concentrate — the SMBS-freshness penalty driver (v19 M12 §7). */
  pbInZnConcPct: number;
};

/** Complete production days, 1–7 Jun 2026 (month to date) — D25. `date` is the
 *  **production day**, and its reading is stamped 18:00 the evening before
 *  (`stateForDay` maps the production day to that stamp). The window is the one
 *  shared June-to-date function's (`shared/june-tonnage.derive.ts`) complete-day
 *  window, so `/cost`'s month is production days 1–7 — readings stamped 31 May
 *  18:00 – 06 Jun 18:00 — and its per-day quantities sum to the function's
 *  12,401 dst at the demo cursor.
 *
 *  Week to date is 2–7 (the six complete days inside the 02–08 Jun production
 *  week; 08 Jun is open at the demo cursor and carries no closed cost/revenue
 *  figures); yesterday is the last complete day, 07 Jun.
 *
 *  MF-6 fix (a20-cost fixer round): tonnesMilled, recoveries and concGrades
 *  are no longer an invented ramp — they are read straight off the shared
 *  daily series (`rs-ore-td` / `rs-zn-rec` / `rs-pb-rec` / `rs-ag-rec` /
 *  `rs-zn-grade` / `rs-pb-grade` / `rs-ag-grade`, `series.derive.ts`) at each
 *  production day's own 18:00-the-evening-before stamp, so Metal accounting,
 *  Reports and the GM landing read the same production days this tab does.
 *
 *  K20 round 1 (#717): the `ag` column took the same +0.84 the `rs-ag-grade`
 *  series took when the reading moved 11.3 → 12.14 (11.8→12.64, 11.7→12.54,
 *  12.0→12.84, 12.1→12.94, 11.8→12.64, 11.9→12.74, 11.6→12.44), so the column
 *  tracks the reading rather than a stale pre-K20 copy.
 *
 *  Issue #729 first pointed the rendered Ag grade at Metal accounting's MTD
 *  balance (the back-calculated 11.24); issue #765 reverses that: the rendered
 *  grade is the balance's **assayed** product grade — the daily `rs-ag-grade`
 *  assay mass-weighted by each production day's Pb/Ag concentrate dmt, 12.65
 *  oz/t month to date and 12.69 for the week — so this column is the
 *  unmeasured-balance fallback only.
 *
 *  No money moves in either read: `settleAtShortTonFeed` prices silver from
 *  head × recovery (`Scene.transform.ts` `revenueForInputs` — the settlement's
 *  `concentrateGrades` carries Pb and Zn only), so only the displayed grade
 *  moves.
 *
 *  D25 round 2: the window moved from the readings stamped 01–07 Jun
 *  (production days 2–8, including the open 08 Jun day shift) to the complete
 *  production days 1–7. The rows for production days 2–7 are the readings
 *  stamped 01–06 Jun (unchanged values); production day 1 Jun is the reading
 *  stamped 31 May 18:00 (1,749 dst, Zn/Pb/Ag recovery 82.9/84.8/83.0, Zn
 *  concentrate grade 57.7); the reading stamped 07 Jun (production day 08,
 *  the open day) is out. The live-cursor "reference day" special case is gone:
 *  every row now reads its own stamp, so no row mixes a production day's
 *  tonnage with the app cursor's assays. pbInZnConcPct remains the IO-01 demo
 *  ramp (not a shared series) and runs across the window's seven production
 *  days in order. */
export const COST_DAYS: readonly CostDaySeed[] = [
  {
    date: '2026-06-01',
    tonnesMilled: 1749,
    recoveries: { zn: 82.9, pb: 84.8, ag: 83.0 },
    concGrades: { zn: 57.7, pb: 65.9, ag: 12.64 },
    pbInZnConcPct: 2.1,
  },
  {
    date: '2026-06-02',
    tonnesMilled: 1771,
    recoveries: { zn: 82.8, pb: 85.3, ag: 83.4 },
    concGrades: { zn: 58.5, pb: 66.3, ag: 12.54 },
    pbInZnConcPct: 2.3,
  },
  {
    date: '2026-06-03',
    tonnesMilled: 1756,
    recoveries: { zn: 82.7, pb: 85.0, ag: 83.1 },
    concGrades: { zn: 58.1, pb: 66.9, ag: 12.84 },
    pbInZnConcPct: 2.6,
  },
  {
    date: '2026-06-04',
    tonnesMilled: 1820,
    recoveries: { zn: 82.6, pb: 85.1, ag: 83.4 },
    concGrades: { zn: 57.6, pb: 67.1, ag: 12.94 },
    pbInZnConcPct: 3.3,
  },
  {
    date: '2026-06-05',
    tonnesMilled: 1749,
    recoveries: { zn: 82.5, pb: 84.9, ag: 83.3 },
    concGrades: { zn: 57.9, pb: 67.5, ag: 12.64 },
    pbInZnConcPct: 3.9,
  },
  {
    date: '2026-06-06',
    tonnesMilled: 1791,
    recoveries: { zn: 82.4, pb: 85.2, ag: 83.5 },
    concGrades: { zn: 58.6, pb: 67.4, ag: 12.74 },
    pbInZnConcPct: 3.5,
  },
  {
    date: '2026-06-07',
    tonnesMilled: 1765,
    recoveries: { zn: 82.4, pb: 85.1, ag: 83.6 },
    concGrades: { zn: 58.0, pb: 66.7, ag: 12.44 },
    pbInZnConcPct: 2.4,
  },
];

/** Month-4 ramp Budget — 1,440 t/d (60 t/h), flat across the seeded month.
 *  Cost-side Budget only (build-up unit rates stay [syn] seed, MF-6 ruling);
 *  revenue Budget instead follows the NsrEngine basis (actual tonnes ×
 *  design recoveries) via `nsrRead().budgetDaily`, the same figure the GM
 *  landing's "target" reads — see `Scene.transform.ts` `budgetRevenueForDay`.
 *
 *  The value aliases the one daily plan constant in
 *  `shared/june-tonnage.derive.ts`, so the bridge's 7-day budget (10,080 dst)
 *  and the landing tile's 8-day plan (11,520 dst) cannot drift. */
export const BUDGET_TONNES_PER_DAY = JUNE_DAILY_PLAN_DST;

// ---------------------------------------------------------------------------
// Cost build-up unit costs
// ---------------------------------------------------------------------------

export type ReagentUnitCost = {
  id: string;
  name: string;
  doseGramsPerTonne: number;
  ratePerKg: number;
  basis: string;
};

/** Dose rates are documented (v19 M12 §4); $/kg landed rates are [syn] pending client procurement data (M12 §2). */
export const REAGENT_UNIT_COSTS: readonly ReagentUnitCost[] = [
  { id: 'znso4', name: 'ZnSO4', doseGramsPerTonne: 500, ratePerKg: 1.85, basis: 'Pb conditioner + cleaner 1' },
  { id: 'smbs', name: 'SMBS', doseGramsPerTonne: 475, ratePerKg: 0.95, basis: 'Pb circuit + Zn cleaner 1' },
  { id: 'cuso4', name: 'CuSO4', doseGramsPerTonne: 500, ratePerKg: 2.40, basis: 'Zn conditioner' },
  { id: '3418a', name: '3418A', doseGramsPerTonne: 50, ratePerKg: 4.20, basis: 'Pb rougher + 3 cleaner stages' },
  { id: 'aero5100', name: 'Aero 5100', doseGramsPerTonne: 37.5, ratePerKg: 3.60, basis: 'Zn rougher + 3 cleaner stages' },
  { id: 'lime', name: 'Lime', doseGramsPerTonne: 705, ratePerKg: 0.18, basis: 'Zn conditioning + tails thickener' },
  { id: 'flocculant', name: 'Flocculant', doseGramsPerTonne: 45, ratePerKg: 3.10, basis: 'Pb, Zn and tails thickeners (3× 15 g/t)' },
];

/** No consumption-rate study exists yet (M12 §6) — kg/t and $/t both [syn]. */
export const GRINDING_MEDIA_UNIT_COST = { kgPerTonne: 0.65, ratePerTonne: 1450 };

/** BM01 continuous draw is documented (~875 kW, ≈21,000 kWh/day, M12 §5); balance-of-plant
 *  kWh/t and the $/kWh tariff are [syn]. */
export const POWER_UNIT_COST = {
  bm01KwhPerDay: 21_000,
  balanceOfPlantKwhPerTonne: 45.0,
  ratePerKwh: 0.11,
};

/** No labour cost/headcount rate exists in the corpus (M12 §6) — shift count and $/shift both [syn]. */
export const LABOUR_UNIT_COST = { shiftsPerDay: 2, ratePerShift: 28_000 };

/** Maintenance consumables spend is a client-budget estimate, not bottom-up (M12 §6) — [syn].
 *  Production days 06–07 Jun carry the BM01 trunnion-bearing inspection parts
 *  spend (WO-441). The dates are `COST_DAYS` production days (D25), so both
 *  bumped days sit inside the month window. */
export const MAINTENANCE_UNIT_COST = {
  ratePerDay: 5_200,
  bumpRatePerDay: 12_000,
  bumpDates: new Set(['2026-06-06', '2026-06-07']),
};

// ---------------------------------------------------------------------------
// Improvement opportunities (M13)
// ---------------------------------------------------------------------------

export type ImprovementCategory = 'capacity' | 'grade' | 'recovery' | 'penalty' | 'cost';
export type ImprovementStage = 'candidate' | 'priced' | 'capital request' | 'approved';

export type BenefitTerm = { label: string; amountPerYear: number };

export type ImprovementOpportunitySeed = {
  id: string;
  opportunity: string;
  category: ImprovementCategory;
  evidence: string;
  benefitTerms: readonly BenefitTerm[];
  capex?: number;
  stage: ImprovementStage;
  exploitSubordinateElevate: { tried: boolean; note: string };
};

export const IMPROVEMENT_OPPORTUNITIES: readonly ImprovementOpportunitySeed[] = [
  {
    id: 'IO-01',
    opportunity: 'SMBS batch-age discipline',
    category: 'penalty',
    evidence:
      `Pb-in-Zn concentrate penalty recurred on 3 of the last 7 closed days (up to ${CURRENCY_GLYPH}14.4K/day) as SMBS batch age crept past the 5-day supply guidance; the round-sheet check now carries the batch-age field.`,
    benefitTerms: [{ label: 'Avoided Zn concentrate Pb-penalty exposure', amountPerYear: 110_000 }],
    capex: 4_500,
    stage: 'approved',
    exploitSubordinateElevate: { tried: true, note: 'Exploit — round-sheet discipline only, no capital' },
  },
  {
    id: 'IO-02',
    opportunity: 'Cyclone cluster capacity',
    category: 'capacity',
    evidence:
      '4 of 5 duty cyclones are in service at design rate, leaving exactly one spare — the least spare capacity anywhere in the plant; grinding is the constraint.',
    benefitTerms: [{ label: 'Throughput ceiling above the month-4 ramp', amountPerYear: 240_000 }],
    capex: 650_000,
    stage: 'priced',
    exploitSubordinateElevate: { tried: false, note: 'Elevate — feed-size control (Exploit) not yet exhausted' },
  },
  {
    id: 'IO-03',
    opportunity: 'Finer grind trade-off',
    category: 'recovery',
    evidence:
      'Global grind coarsening is the single most impactful yield loss in the plant; design P80 target is 74 µm. Grinding already has the least spare residence time of any subprocess.',
    benefitTerms: [
      { label: 'Recovery/grade gain from finer grind', amountPerYear: 165_000 },
      { label: 'Throughput loss from added residence time', amountPerYear: -95_000 },
    ],
    capex: 180_000,
    stage: 'priced',
    exploitSubordinateElevate: {
      tried: false,
      note: 'Subordinate — finer grind competes with throughput at the same constraint',
    },
  },
  {
    id: 'IO-04',
    opportunity: 'Grinding media consumption reduction',
    category: 'cost',
    evidence:
      'Daily ball-charge tracking and the weekly media survey show consumption drifting above the 0.65 kg/t baseline.',
    benefitTerms: [{ label: 'Reduced grinding-media spend', amountPerYear: 42_000 }],
    capex: 35_000,
    stage: 'priced',
    exploitSubordinateElevate: { tried: true, note: 'Exploit — ball-charge tracking discipline tried first' },
  },
  {
    id: 'IO-05',
    opportunity: 'Regrind mill drive reliability',
    category: 'capacity',
    evidence: 'Regrind mill drive tripping on overload (3W-109) opened CCC-11 — recurring beyond procedural fixes.',
    benefitTerms: [{ label: 'Avoided regrind-mill downtime', amountPerYear: 130_000 }],
    capex: 210_000,
    stage: 'capital request',
    exploitSubordinateElevate: { tried: true, note: 'Exploit/Subordinate tried — interim condition-based limits set' },
  },
  {
    id: 'IO-06',
    opportunity: 'Zn cleaner pH control retune',
    category: 'recovery',
    evidence: 'Zn recovery is tracking below the design/Budget gap the pH control loop was sized to close.',
    benefitTerms: [{ label: 'Zn recovery gain from tighter pH control', amountPerYear: 58_000 }],
    capex: 18_000,
    stage: 'priced',
    exploitSubordinateElevate: { tried: true, note: 'Exploit — loop retune, no new equipment' },
  },
  {
    id: 'IO-07',
    opportunity: 'Filter cloth interval extension',
    category: 'cost',
    evidence: 'Filter-cloth change frequency is running ahead of the condition-based interval the media supports.',
    benefitTerms: [{ label: 'Reduced filter-cloth replacement frequency', amountPerYear: 26_000 }],
    capex: 12_000,
    stage: 'priced',
    exploitSubordinateElevate: { tried: true, note: 'Exploit — interval extension only' },
  },
  {
    id: 'IO-08',
    opportunity: 'Pb rougher level loop auto availability',
    category: 'recovery',
    evidence:
      'LIC4010 has held Pb rougher cell 1 level in manual for 8.2 h, above the 4 h control-loop exception threshold, changed 03:20 by the CRO; the met meeting carries it as a standing control-loop exception.',
    benefitTerms: [],
    capex: 0,
    stage: 'candidate',
    exploitSubordinateElevate: { tried: false, note: 'Not yet assessed' },
  },
  {
    id: 'IO-09',
    opportunity: 'Ball mill grate wear interval review',
    category: 'recovery',
    evidence:
      'Mill discharge grates worn or blocked is the diagnosed cause on the BM01 discharge-coarsening case; the grate change interval is calendar-based, not wear-based.',
    benefitTerms: [],
    capex: 0,
    stage: 'candidate',
    exploitSubordinateElevate: { tried: false, note: 'Not yet assessed' },
  },
  {
    id: 'IO-10',
    opportunity: 'Zn flotation capacity (rougher residence time)',
    category: 'capacity',
    evidence: 'The current circuit sequence is read from CapacityEngine.',
    benefitTerms: [],
    stage: 'priced',
    exploitSubordinateElevate: { tried: false, note: 'Elevate — rougher residence time requires additional capacity' },
  },
];
